import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { UserRole } from '../types';
import { ICONS } from '../constants';
import { API_CONFIG } from '../config';
import Button from './ui/Button';
import Input from './ui/Input';
import LanguageToggle from './LanguageToggle';
import { useTranslation } from '../hooks/useTranslation';
import { User } from '../types';
import SimpleCaptcha from './SimpleCaptcha';

interface LoginScreenProps {
  onLogin: (userData: User, token: string) => void;
}

const MAX_ATTEMPTS = 5;
const LOCKOUT_DURATION = 60; // seconds

const LoginScreen: React.FC<LoginScreenProps> = ({ onLogin }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  
  // Rate limiting states
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  
  // Password reset states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [captchaValid, setCaptchaValid] = useState(false);

  // Check for existing lockout on mount
  useEffect(() => {
    const lockoutData = localStorage.getItem('loginLockout');
    if (lockoutData) {
      const { lockUntil, attempts } = JSON.parse(lockoutData);
      const now = Date.now();
      
      if (lockUntil > now) {
        setIsLocked(true);
        setFailedAttempts(attempts);
        setRemainingTime(Math.ceil((lockUntil - now) / 1000));
      } else {
        // Lockout expired, clear it
        localStorage.removeItem('loginLockout');
      }
    }
  }, []);

  // Countdown timer
  useEffect(() => {
    if (isLocked && remainingTime > 0) {
      const timer = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            setIsLocked(false);
            setFailedAttempts(0);
            localStorage.removeItem('loginLockout');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isLocked, remainingTime]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if locked
    if (isLocked) {
      setError(`${t('login.tooManyAttempts')} ${remainingTime} ${t('login.seconds')}.`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const requestBody = { email, password };
      
      const response = await fetch(API_CONFIG.AUTH.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (response.ok) {
        // Reset failed attempts on successful login
        setFailedAttempts(0);
        localStorage.removeItem('loginLockout');
        onLogin(data.user, data.accessToken);
      } else {
        console.error('❌ Login failed:', data);
        
        // Increment failed attempts
        const newAttempts = failedAttempts + 1;
        setFailedAttempts(newAttempts);
        
        if (newAttempts >= MAX_ATTEMPTS) {
          // Lock the user out
          const lockUntil = Date.now() + (LOCKOUT_DURATION * 1000);
          localStorage.setItem('loginLockout', JSON.stringify({
            lockUntil,
            attempts: newAttempts
          }));
          setIsLocked(true);
          setRemainingTime(LOCKOUT_DURATION);
          setError(`${t('login.tooManyAttempts')} ${LOCKOUT_DURATION} ${t('login.seconds')}.`);
        } else {
          const attemptsLeft = MAX_ATTEMPTS - newAttempts;
          setError(`${data.error || data.message || t('login.invalidCredentials')}. ${attemptsLeft} ${t('login.attemptsRemaining')}.`);
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Connection error. Please check if the backend server is running.');
    } finally {
      setLoading(false);
    }
  };

  // Email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (value && !validateEmail(value)) {
      setEmailError(t('login.emailValidationError'));
    } else {
      setEmailError('');
    }
  };

  // Password reset functions
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail(resetEmail)) {
      setResetMessage(t('login.emailValidationError'));
      return;
    }

    if (!captchaValid) {
      setResetMessage(t('login.recaptchaRequired'));
      return;
    }

    setResetLoading(true);
    setResetMessage('');

    try {
      const response = await fetch(API_CONFIG.AUTH.FORGOT_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: resetEmail,
          recaptchaToken: 'simple-captcha-validated' // Простая капча всегда возвращает этот токен
        }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.resetUrl) {
          // In development, show the reset URL
          setResetMessage(t('login.resetSuccess') + `\n\nСсылка для сброса: ${data.resetUrl}`);
        } else {
          setResetMessage(t('login.resetSuccess'));
        }
      } else {
        setResetMessage(data.error || t('login.resetError'));
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setResetMessage(t('login.resetConnectionError'));
    } finally {
      setResetLoading(false);
    }
  };

  const handleCaptchaChange = (isValid: boolean) => {
    setCaptchaValid(isValid);
  };

  const handleCaptchaExpired = () => {
    setCaptchaValid(false);
  };

  const resetCaptcha = () => {
    setCaptchaValid(false);
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-900 relative">
      {/* Vignette overlay */}
      <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-black/30 pointer-events-none"></div>
      <div className="relative z-10 w-full max-w-md p-8 space-y-8 bg-gray-800 rounded-lg shadow-lg">
        <div className="flex flex-col items-center">
          <div className="flex items-center space-x-3">
            <ICONS.BookOpen className="w-14 h-14 text-indigo-400" />
            <h1 className="text-4xl font-bold text-center text-white">{t('login.title')}</h1>
          </div>
          <p className="mt-2 text-center text-sm text-gray-400">{t('login.subtitle')}</p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleLogin}>
          <div>
            <Input
              label={t('login.email')}
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={handleEmailChange}
              placeholder={t('login.emailPlaceholder')}
            />
            {emailError && <p className="text-sm text-red-500 mt-1">{emailError}</p>}
          </div>
          <div className="mt-4">
             <Input
              label={t('login.password')}
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('login.passwordPlaceholder')}
            />
          </div>
          
          {error && <p className="text-sm text-red-500 text-center pt-2">{error}</p>}
          
          {/* Lockout warning */}
          {isLocked && (
            <div className="bg-yellow-900/30 border border-yellow-600/50 rounded p-3 flex items-center space-x-2">
              <ICONS.AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
              <div className="text-sm text-yellow-200">
                <strong>{t('login.accountLocked')}</strong>
                <p className="text-xs mt-1">
                  {t('login.tooManyAttempts')} <span className="font-bold">{remainingTime}</span> {t('login.seconds')}.
                </p>
              </div>
            </div>
          )}

          <div className="pt-4">
            <Button type="submit" className="w-full justify-center" disabled={loading || isLocked}>
              {loading ? '...' : isLocked ? `${t('login.accountLocked')} (${remainingTime}s)` : t('login.loginButton')}
            </Button>
          </div>

          {/* Forgot Password Link */}
          <div className="text-center space-y-2">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-indigo-400 hover:text-indigo-300 underline"
            >
              {t('login.forgotPassword')}
            </button>
            <div>
              <Link
                to="/register"
                className="text-sm text-indigo-400 hover:text-indigo-300 underline"
              >
                {t('login.registerLink')}
              </Link>
            </div>
          </div>
        </form>

        {/* Language Toggle - Bottom */}
        <div className="flex justify-center">
          <LanguageToggle />
        </div>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-md mx-4">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('login.resetPassword')}</h2>
              
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label htmlFor="resetEmail" className="block text-sm font-medium text-gray-300 mb-1">
                    {t('login.resetEmail')}
                  </label>
                  <Input
                    label=""
                    id="resetEmail"
                    name="resetEmail"
                    type="email"
                    required
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder={t('login.resetEmailPlaceholder')}
                  />
                </div>

                {/* Simple Captcha */}
                <SimpleCaptcha
                  onValidate={handleCaptchaChange}
                />

                {resetMessage && (
                  <p className={`text-sm text-center ${
                    resetMessage.includes(t('login.resetSuccess')) ? 'text-green-600' : 'text-red-500'
                  }`}>
                    {resetMessage}
                  </p>
                )}

                <div className="flex space-x-3">
                  <Button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetEmail('');
                      setResetMessage('');
                      resetCaptcha();
                    }}
                    className="flex-1 bg-gray-600 hover:bg-gray-700"
                  >
                    {t('login.resetCancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={resetLoading}
                    className="flex-1"
                  >
                    {resetLoading ? t('login.resetSending') : t('login.resetSend')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginScreen;
